/**
 * FileSizeDisplay — a byte count rendered in the largest sensible unit, localized.
 *
 * Formatting goes through `Intl.NumberFormat`'s `style: 'unit'` (via the user's
 * `formatNumber`), so the numeral separators AND the unit label both follow the
 * viewer's locale — no hand-built "1.5 MB" string and no i18n keys per unit.
 *
 * Units step in 1024s and are labelled with the decimal names (KB/MB/GB/TB),
 * matching what every desktop OS shows and what the storage quota is expressed
 * in. Bytes are a Class B bidi token (digits + separators), which `LtrToken`
 * pins LTR so the value doesn't scramble under RTL.
 */

import { useUserPreferences } from '../../context';
import { LtrToken } from '../../components/common/LtrToken';

const STEP = 1024;

/** Intl unit identifiers, ascending. Index n covers STEP**n bytes. */
const UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'] as const;

export interface FileSizeDisplayProps {
  /** Byte count. `null`/`undefined` renders `emptyText`. */
  bytes: number | null | undefined;
  emptyText?: string;
  className?: string;
}

/**
 * Pick the largest unit that leaves the value at or above 1, and the fraction
 * digits that read well there: whole bytes and kilobytes, one decimal above.
 */
export function formatFileSize(
  bytes: number,
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string
): string {
  const magnitude = Math.min(
    UNITS.length - 1,
    bytes > 0 ? Math.floor(Math.log(bytes) / Math.log(STEP)) : 0
  );
  const value = bytes / STEP ** magnitude;
  return formatNumber(value, {
    style: 'unit',
    unit: UNITS[magnitude],
    unitDisplay: 'short',
    maximumFractionDigits: magnitude <= 1 ? 0 : 1,
  });
}

export function FileSizeDisplay({ bytes, emptyText = '-', className }: FileSizeDisplayProps) {
  const { formatNumber } = useUserPreferences();

  if (bytes === null || bytes === undefined) {
    return <span className={className}>{emptyText}</span>;
  }

  return <LtrToken className={className}>{formatFileSize(bytes, formatNumber)}</LtrToken>;
}
